<script
  setup
  lang="ts"
  generic="T extends string | number | object, A extends LoaderFnArgs = LoaderFnArgs"
>
  import { computed } from 'vue';
  import type { HTMLAttributes } from 'vue';

  import { Button } from '@ui/components/button';
  import type { ButtonVariants } from '@ui/components/button';
  import type { Loader, LoaderFnArgs } from '@ui/components/combo-select/useLoader';
  import { Popover, PopoverContent, PopoverTrigger } from '@ui/components/popover';
  import { cn } from '@ui/utils/utils';

  import ComboCommand from './ComboCommand.vue';
  import type { CommandGrouping } from './ComboCommand.vue';

  interface ComboSelectProps {
    additionalOptions?: T[];
    btnClass?: HTMLAttributes['class'];
    clearable?: boolean;
    disabled?: boolean;
    disabledFn?: (option: T) => boolean;
    dropdownClass?: HTMLAttributes['class'];
    exclude?: (string | number)[];
    filter?: (searchTerm?: string) => Promise<T[]>;
    groupBy?: (item: T) => string;
    groupHeadingClass?: HTMLAttributes['class'];
    groups?: CommandGrouping[];
    hideArrow?: boolean;
    inPlaceSearch?: boolean;
    keepSelectedInPlace?: boolean;
    lazyLoad?: boolean;
    loader?: Loader<T, A> | null;
    loadOnOpen?: boolean;
    multiple?: boolean;
    noSave?: boolean;
    optionClass?: HTMLAttributes['class'];
    optionIdentifier?: string;
    options?: T[];
    outline?: boolean;
    placeholder?: string;
    popoverAlign?: 'start' | 'center' | 'end';
    popoverSide?: 'top' | 'right' | 'bottom' | 'left';
    search?: string;
    searchable?: boolean;
    searchAttrs?: string[];
    searchPlaceholder?: string;
    sectionHeading?: string;
    showCheckIcon?: boolean;
    showSearchIcon?: boolean;
    showSelectedAtTop?: boolean;
    size?: ButtonVariants['size'];
    transparent?: boolean;
    variant?: 'ghost' | 'dropdown' | 'filter';
  }

  const props = withDefaults(defineProps<ComboSelectProps>(), {
    options: () => [],
    placeholder: 'Select Value',
    popoverAlign: 'start',
    popoverSide: 'right',
    searchPlaceholder: 'Search...',
    sideOffset: 6,
    size: 'sm',
    variant: 'ghost',
  });

  const emits = defineEmits<{ (e: 'select', option: T) }>();
  const modelValue = defineModel<T | T[] | null>({ default: null });
  const open = defineModel<boolean>('open', { default: false });

  const internalValue = computed<T[]>(() =>
    modelValue.value
      ? Array.isArray(modelValue.value)
        ? modelValue.value
        : [modelValue.value]
      : [],
  );

  const triggerVariant = computed(() => {
    if (props.transparent) return 'ghost';
    if (props.variant === 'dropdown') return 'outline';

    return 'ghost';
  });

  const valueKeys = computed<Set<number | string>>(
    () => new Set([...internalValue.value.map((v) => getOptionValue(v))]),
  );

  const getOptionValue = (option: T): number | string => {
    return props.optionIdentifier ? option[props.optionIdentifier] : (option as string | number);
  };
</script>

<template>
  <div
    v-bind="$attrs"
    :class="cn('flex items-center truncate rounded-sm', variant === 'filter' && 'border h-8')"
  >
    <template v-if="variant === 'filter' && internalValue?.length">
      <div class="flex text-xs gap-2 px-3 items-center font-medium h-full border-l-rounded">
        <slot name="filter-label" />
      </div>
    </template>

    <Popover v-model:open="open">
      <PopoverTrigger as-child>
        <slot name="trigger">
          <Button
            :variant="triggerVariant"
            :class="
              cn(
                '*:overflow-hidden *:overflow-ellipsis',
                {
                  'justify-between': variant === 'dropdown',
                  'justify-start': variant !== 'dropdown',
                },
                variant === 'filter' && internalValue?.length && 'rounded-none border-l',
                btnClass,
              )
            "
            :size="size"
            role="combobox"
            :aria-expanded="open"
            :disabled="disabled"
          >
            <template v-if="variant === 'filter' && !internalValue?.length">
              <slot name="filter-label">
                <span>
                  {{ placeholder }}
                </span>
              </slot>
            </template>

            <template v-else-if="internalValue.length">
              <span>
                <slot name="selected" :value="modelValue as T">
                  {{ internalValue?.length ? Array.from(valueKeys).join(' | ') : placeholder }}
                </slot>
              </span>
            </template>

            <template v-else>
              <slot name="placeholder">
                <span>
                  {{ placeholder }}
                </span>
              </slot>
            </template>

            <i v-if="variant === 'dropdown' && !hideArrow" class="fal fa-chevron-down text-3xs!" />
          </Button>
        </slot>
      </PopoverTrigger>

      <PopoverContent
        :class="cn('p-0 max-w-full', dropdownClass)"
        :side="popoverSide"
        :align="popoverAlign"
      >
        <ComboCommand
          v-bind="props"
          v-model="modelValue"
          v-model:open="open"
          @select="(args) => emits('select', args)"
        >
          <template #selected="slotProps">
            <slot name="selected" v-bind="slotProps" />
          </template>
          <template #empty="slotProps">
            <slot name="empty" v-bind="slotProps" />
          </template>
          <template #option="slotProps">
            <slot name="option" v-bind="slotProps" />
          </template>
          <template v-if="$slots.fixedTop" #fixed="slotProps">
            <slot name="fixed" v-bind="slotProps" />
          </template>
          <template v-if="$slots.fixedBottom" #fixedBottom="slotProps">
            <slot name="fixedBottom" v-bind="slotProps" />
          </template>
        </ComboCommand>
      </PopoverContent>
    </Popover>

    <template v-if="variant === 'filter' && internalValue?.length">
      <Button
        variant="ghost"
        :size="size"
        class="rounded-l-none border-l px-2"
        @click="modelValue = null"
      >
        <i class="far fa-xmark text-2xs" />
      </Button>
    </template>
  </div>
</template>

<style scoped></style>
